import React, { useState, useEffect } from "react";
import { Lock, Mail, Eye, EyeOff, User, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import LoadingButton from "../LoadingButton/LoadingButton";
import { login, redirectBasedOnRole } from "../../services/authService";
import "./LoginSystem.css";
const API_BASE_URL = "https://alternative-lamprey-fatma-celik-a9c7fc96.koyeb.app";
const LoginSystem = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [isResetLoading, setIsResetLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Önceki oturum kalıntılarını temizle
    sessionStorage.clear();

    // Çıkış sonrası geldiğini kontrol et
    const isAfterLogout = sessionStorage.getItem("isAfterLogout");
    if (isAfterLogout) {
      sessionStorage.removeItem("isAfterLogout");
      return;
    }

    // BiLiM logosu animasyonunu başlat
    const logoElement = document.querySelector(".login-logo");
    if (logoElement) {
      logoElement.style.animation = "fadeInUp 0.8s ease-out forwards";
    }

    // Form animasyonunu başlat
    const formElement = document.querySelector(".login-form");
    if (formElement) {
      formElement.style.animation = "fadeInUp 0.8s ease-out 0.3s forwards";
    }

    // Demo bilgileri animasyonunu başlat
    const demoElement = document.querySelector(".demo-info");
    if (demoElement) {
      demoElement.style.animation = "fadeInUp 0.8s ease-out 0.6s forwards";
    }

    // Satürn halkası animasyonunu başlat
    const saturnRing = document.querySelector(".saturn-ring-container");
    if (saturnRing) {
      saturnRing.style.animation = "rotate 20s linear infinite";
    }

    // Gezegen animasyonlarını başlat
    const planets = document.querySelectorAll(".orbit-planet");
    planets.forEach((planet, index) => {
      planet.style.animation = `orbit ${8 + index * 2}s linear infinite`;
    });
  }, []);

  // BiLiM logosu bileşeni
  const LogoComponent = () => (
    <div className="login-logo">
      <div className="login-logo-circles">
        <div className="circle circle-red"></div>
        <div className="circle circle-yellow"></div>
        <div className="circle circle-blue"></div>
      </div>
      <div className="login-logo-text">
        <h1>BİLİM</h1>
        <h2>KONYA</h2>
        <p>Personel Takip Sistemi</p>
      </div>
    </div>
  );

  // Giriş yapma fonksiyonu
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const result = await login(email, password);
      if (result.success) {
        // Rol bazlı yönlendirme
        const redirectPath = redirectBasedOnRole(result.user);
        console.log("Yönlendiriliyor:", redirectPath);
        // Yönlendirmeyi bir sonraki event loop'a ertele
        setTimeout(() => {
          navigate(redirectPath);
        }, 100);
      } else {
        setError(result.error || "Giriş yapılırken hata oluştu");
      }
    } catch (err) {
      setError(err.message || "Giriş yapılırken hata oluştu");
    } finally {
      setIsLoading(false);
    }
  };

  // Şifre sıfırlama talebi gönderme
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsResetLoading(true);
    setResetMessage("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: forgotPasswordEmail }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setResetMessage(
          "Şifre sıfırlama bağlantısı e-posta adresinize gönderildi."
        );
      } else {
        setResetMessage(data.error || "Bir hata oluştu.");
      }
    } catch (err) {
      setResetMessage("Sunucu hatası. Lütfen daha sonra tekrar deneyin.");
    } finally {
      setIsResetLoading(false);
    }
  };

  // Şifre görünürlüğünü değiştirme
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      {/* Tek büyük Satürn halkası - login panelinin arkasında */}
      <div className="saturn-ring-container">
        {/* Ana halka */}
        <div className="main-saturn-ring">
          {/* Sadece 3 ana renkli gezegen - daire çizgisinin üstünde */}
          <div className="orbit-planet orbit-planet-red"></div>
          <div className="orbit-planet orbit-planet-yellow"></div>
          <div className="orbit-planet orbit-planet-blue"></div>
        </div>
        {/* İç halka */}
        <div className="inner-ring"></div>
        {/* Dış halka */}
        <div className="outer-ring"></div>
      </div>

      {/* Login konteyneri - halkanın üstünde */}
      <div className="login-container">
        <div className="login-form">
          <LogoComponent />

          {showForgotPassword ? (
            <form
              onSubmit={handleForgotPassword}
              className="login-form-content"
            >
              <h3>Şifrenizi mi Unuttunuz?</h3>
              {resetMessage && (
                <div className="info-message">{resetMessage}</div>
              )}
              <div className="form-group">
                <div className="input-group">
                  <input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="E-posta adresiniz"
                    className="form-input"
                    required
                  />
                  <Mail className="input-icon" />
                </div>
              </div>
              <LoadingButton
                type="submit"
                isLoading={isResetLoading}
                className="login-button"
              >
                {isResetLoading
                  ? "Gönderiliyor..."
                  : "Şifre Sıfırlama Bağlantısı Gönder"}
              </LoadingButton>
              <button
                type="button"
                className="text-button"
                onClick={() => setShowForgotPassword(false)}
              >
                Girişe Dön
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="login-form-content">
              {error && <div className="error-message">{error}</div>}
              <div className="form-group">
                <div className="input-group">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email adresiniz"
                    className="form-input"
                    required
                  />
                  <Mail className="input-icon" />
                </div>
              </div>
              <div className="form-group">
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Şifreniz"
                    className="form-input"
                    required
                  />
                  <Lock className="input-icon" />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="password-toggle"
                    aria-label={
                      showPassword ? "Şifreyi gizle" : "Şifreyi göster"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <LoadingButton
                type="submit"
                isLoading={isLoading}
                className="login-button"
              >
                {isLoading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </LoadingButton>
              <button
                type="button"
                className="text-button"
                onClick={() => setShowForgotPassword(true)}
              >
                Şifremi Unuttum
              </button>
            </form>
          )}
        </div>  
      </div>
    </div>
  );
};

export default LoginSystem;
